import { useState } from "react";
import { Code, Pencil, Plus, Trash2 } from "lucide-react";
import { Button, Dialog, IconButton } from "@dfws/ui";
import type { Signature } from "@dfws/contracts";
import { api, ApiFailure } from "../../lib/api";
import { queryClient } from "../../lib/queries";
import { useSignatures } from "../compose/composeData";
import { Editor } from "../compose/Editor";
import s from "./Settings.module.css";

/** Several signatures, one default for new mail and one for replies. HTML signatures (logos, tables) can be pasted as source. */
export function SignaturesSection() {
  const sigs = useSignatures();
  const [editing, setEditing] = useState<Partial<Signature> | null>(null);
  const items = sigs.data?.items ?? [];
  const remove = async (id: number) => {
    await api(`/signatures/${id}`, { method: "DELETE" });
    void queryClient.invalidateQueries({ queryKey: ["signatures"] });
  };
  return (
    <>
      <p className={s.lead}>Your signature is added below what you write and above the quoted message. Choose a different one, or none, in each message.</p>
      <div className={s.actionsBar} style={{ marginTop: 0, marginBottom: "var(--space-4)" }}>
        <Button variant="primary" icon={<Plus />} onClick={() => setEditing({ name: items.length ? "" : "Default", html: "", defaultNew: !items.length, defaultReply: !items.length })}>
          New signature
        </Button>
      </div>
      {sigs.isPending ? (
        <p className={s.muted}>Loading signatures...</p>
      ) : items.length === 0 ? (
        <div className={s.empty}>No signature yet.</div>
      ) : (
        <ul className={s.list}>
          {items.map((x) => (
            <li key={x.id} className={s.listItem} style={{ alignItems: "flex-start" }}>
              <div className={s.listMain}>
                <div className={s.listTitle}>
                  {x.name}
                  {x.defaultNew && <span className={s.chip} style={{ marginLeft: 8 }}>new mail</span>}
                  {x.defaultReply && <span className={s.chip}>replies</span>}
                </div>
                <div className={s.sigPreview} dangerouslySetInnerHTML={{ __html: x.html }} />
              </div>
              <IconButton label={`Edit ${x.name}`} icon={<Pencil />} size="sm" onClick={() => setEditing(x)} />
              <IconButton label={`Delete ${x.name}`} icon={<Trash2 />} size="sm" onClick={() => void remove(x.id)} />
            </li>
          ))}
        </ul>
      )}
      {editing && <SignatureEditor initial={editing} onClose={() => setEditing(null)} />}
    </>
  );
}

function SignatureEditor({ initial, onClose }: { initial: Partial<Signature>; onClose: () => void }) {
  const [name, setName] = useState(initial.name ?? "");
  const [html, setHtml] = useState(initial.html ?? "");
  const [source, setSource] = useState(false);
  const [defaultNew, setDefaultNew] = useState(initial.defaultNew ?? false);
  const [defaultReply, setDefaultReply] = useState(initial.defaultReply ?? false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    setError("");
    try {
      const body = { name: name.trim(), html, defaultNew, defaultReply };
      await api(initial.id ? `/signatures/${initial.id}` : "/signatures", { method: initial.id ? "PUT" : "POST", body });
      void queryClient.invalidateQueries({ queryKey: ["signatures"] });
      onClose();
    } catch (err) {
      setError(err instanceof ApiFailure ? err.message : "The signature could not be saved.");
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} title={initial.id ? "Edit signature" : "New signature"} size="lg">
      <form className={s.stack} onSubmit={(e) => (e.preventDefault(), void save())}>
        <label className={s.field}>
          <span>Name</span>
          <input className={s.input} value={name} maxLength={80} onChange={(e) => setName(e.target.value)} placeholder="For example, DevFusion" required />
        </label>
        <div className={s.field}>
          <span style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            Signature
            <Button size="sm" variant="ghost" icon={<Code />} onClick={() => setSource((v) => !v)}>{source ? "Visual editor" : "Paste HTML"}</Button>
          </span>
          {source ? (
            <textarea className={s.textarea} value={html} onChange={(e) => setHtml(e.target.value)} spellCheck={false} aria-label="Signature HTML" placeholder="Paste your HTML signature here. Logos must be https links." style={{ fontFamily: "var(--font-mono)", fontSize: "var(--text-xs)" }} />
          ) : (
            <div className={s.sigEditor}>
              <Editor html={html} label="Signature" placeholder="Your name, role, phone" onChange={setHtml} onSubmit={() => void save()} />
            </div>
          )}
        </div>
        {source && html && (
          <div className={s.field}>
            <span>Preview <small>(after saving, unsafe parts such as scripts are removed)</small></span>
            {/* Unsaved HTML is shown in a sandboxed frame (no scripts, no same-origin access) until the server cleans it on save. */}
            <iframe className={s.sigFrame} sandbox="" srcDoc={html} title="Signature preview" />
          </div>
        )}
        <label className={s.check}><input type="checkbox" checked={defaultNew} onChange={(e) => setDefaultNew(e.target.checked)} /> Use for new messages</label>
        <label className={s.check}><input type="checkbox" checked={defaultReply} onChange={(e) => setDefaultReply(e.target.checked)} /> Use for replies and forwards</label>
        {error && <p className={s.err + " " + s.notice} role="alert">{error}</p>}
        <div className={s.actionsBar} style={{ justifyContent: "flex-end" }}>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="primary" type="submit" loading={busy} disabled={!name.trim()}>Save</Button>
        </div>
      </form>
    </Dialog>
  );
}
